// engine-exception: portal tickets — the customer's list (cards/table), the type-driven new-ticket form (user_new field_config) and the public-timeline detail with reply / close / reopen / CSAT; portal principals see a customer workspace, not the agent ticket screen
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useApi } from '../../engine/api/context';
import { Icon } from '../../engine/design/icons';
import { Button } from '../../engine/design/primitives/Button';
import { Field, Input, Select, Textarea } from '../../engine/design/primitives/Input';
import { Avatar, Badge, Banner, Chip, EmptyState, Loading, PageHeader, Skeleton } from '../../engine/design/primitives/Misc';
import { Dialog } from '../../engine/design/primitives/Overlay';
import { DataGrid, type GridColumn } from '../../engine/views/DataGrid';
import { AutoForm } from '../../engine/forms/AutoForm';
import { RichTextView } from '../../engine/fields/RichText';
import type { FieldMeta } from '../../engine/meta/types';
import { formatDate, formatDateTime } from '../../lib/format';
import { safeIcon, usePortal } from '../PortalContext';
import { portalUrl } from '../client';
import { usePortalGet, usePortalItem, usePortalList, usePortalMutation } from '../hooks';
import type { AttachmentRow, FieldConfig, TicketDetail, TicketRow, TicketType, TicketTypesResponse, TimelineEntry } from '../types';
import { BackLink, fmtBytes, ListState, LoadMore, Section, StatusPill, useViewPref, ViewToggle } from '../ui';

const STATUS_CHIPS: { key: string; label: string }[] = [
  { key: '', label: 'All' },
  { key: 'open,in_progress', label: 'Open' },
  { key: 'resolved', label: 'Resolved' },
  { key: 'closed', label: 'Closed' },
];

// ── List ──────────────────────────────────────────────────────────────────────
export function TicketsPage() {
  const { me, can } = usePortal();
  const [sp, setSp] = useSearchParams();
  const status = sp.get('status') ?? '';
  const q = sp.get('q') ?? '';
  const mine = sp.get('mine') === '1';
  const [view, setView] = useViewPref('tickets');
  const list = usePortalList<TicketRow>('tickets', { status: status || undefined, q: q || undefined, mine: mine ? 1 : undefined });
  const set = (k: string, v: string | null) => {
    const next = new URLSearchParams(sp);
    if (v) next.set(k, v);
    else next.delete(k);
    setSp(next, { replace: true });
  };
  const navigate = useNavigate();
  const columns = useMemo<GridColumn<TicketRow>[]>(
    () => [
      { id: 'ref', header: 'Ref', width: 120, cell: (r) => <span className="hf-mono">{r.ref}</span> },
      { id: 'summary', header: 'Summary', minWidth: 240, accessor: (r) => r.summary },
      { id: 'status', header: 'Status', width: 130, cell: (r) => <StatusPill status={r.status_category} label={r.status_name} kind="ticket" /> },
      { id: 'type', header: 'Type', width: 130, accessor: (r) => r.type_name },
      { id: 'contact', header: 'Raised by', width: 160, accessor: (r) => r.contact_name, hidden: me.access_level === 'own' },
      { id: 'updated', header: 'Updated', width: 130, accessor: (r) => formatDate(r.updated_at) },
    ],
    [me.access_level],
  );

  return (
    <div>
      <PageHeader title="Tickets" subtitle={me.access_level === 'own' ? 'Everything you have logged or follow' : `Tickets for ${me.client?.name ?? 'your organisation'}`} actions={can('tickets.create') ? <Link to="/portal/tickets/new" className="hf-btn primary"><Icon name="plus" size={14} /> Log a ticket</Link> : null} />
      <div className="pt-toolbar">
        <Input className="pt-search" placeholder="Search tickets…" value={q} onChange={(e) => set('q', e.target.value || null)} aria-label="Search tickets" />
        <div className="hf-chiprow" role="group" aria-label="Status">
          {STATUS_CHIPS.map((c) => (
            <Chip key={c.key || 'all'} active={status === c.key} role="radio" aria-selected={status === c.key} onClick={() => set('status', c.key || null)}>{c.label}</Chip>
          ))}
          {me.access_level !== 'own' && <Chip active={mine} icon="user" onClick={() => set('mine', mine ? null : '1')}>Mine</Chip>}
        </div>
        <ViewToggle value={view} onChange={setView} />
      </div>
      <ListState loading={list.isLoading} error={list.error} count={list.rows.length} empty={{ title: q || status ? 'No tickets match' : 'No tickets yet', icon: 'ticket', action: can('tickets.create') ? <Link className="hf-btn primary" to="/portal/tickets/new">Log a ticket</Link> : undefined }}>
        {view === 'table' ? (
          <DataGrid<TicketRow> columns={columns} rows={list.rows} keyOf={(r) => r.id} onRowClick={(r) => navigate(`/portal/tickets/${r.id}`)} hasMore={list.hasMore} loadMore={list.loadMore} loadingMore={list.loadingMore} height="auto" />
        ) : (
          <>
            <div className="pt-cards">
              {list.rows.map((r) => (
                <Link key={r.id} to={`/portal/tickets/${r.id}`} className="pt-card">
                  <span className="ref">{r.ref}{r.type_name ? ` · ${r.type_name}` : ''}</span>
                  <span className="title">{r.summary}</span>
                  <span className="meta">
                    {me.access_level !== 'own' && r.contact_name && <span>{r.contact_name}</span>}
                    {r.assignee_name && <span>with {r.assignee_name}</span>}
                    <span>Updated {formatDate(r.updated_at)}</span>
                  </span>
                  <span className="side"><StatusPill status={r.status_category} label={r.status_name} kind="ticket" />{r.priority_name && <small className="hf-muted">{r.priority_name}</small>}</span>
                </Link>
              ))}
            </div>
            <LoadMore hasMore={list.hasMore} loading={list.loadingMore} onMore={list.loadMore} />
          </>
        )}
      </ListState>
    </div>
  );
}

// ── New ───────────────────────────────────────────────────────────────────────
const CUSTOMER_FIELDS: Record<string, Partial<FieldMeta>> = {
  summary: { label: 'What do you need help with?', type: 'text' },
  details: { label: 'Details', type: 'textarea', help: 'What happened, what you expected, and anything you already tried.' },
  category_id: { label: 'Category', type: 'enum' },
  priority_id: { label: 'Priority', type: 'enum' },
  impact_id: { label: 'Impact', type: 'enum' },
  urgency_id: { label: 'Urgency', type: 'enum' },
  due_at: { label: 'Needed by', type: 'datetime' },
};
const ORDER = Object.keys(CUSTOMER_FIELDS);

/** The AutoForm fields for a type's `user_new` config (mirrors applyFieldConfig on the server). */
export function fieldsForType(config: FieldConfig | undefined, catalogs: Pick<TicketTypesResponse, 'categories' | 'priorities'> | undefined): FieldMeta[] {
  const rules = config ?? {};
  const listed = Object.keys(rules).length > 0;
  const out: FieldMeta[] = [];
  for (const name of ORDER) {
    const rule = rules[name];
    const visibility = rule?.visibility ?? (listed ? (name === 'summary' ? 'required' : 'hidden') : name === 'summary' ? 'required' : 'editable');
    if (visibility === 'hidden' || visibility === 'readonly') continue;
    const base = CUSTOMER_FIELDS[name]!;
    let enumOptions: FieldMeta['enum'];
    if (name === 'category_id') enumOptions = (catalogs?.categories ?? []).map((c) => ({ value: c.id, label: c.path }));
    if (name === 'priority_id') enumOptions = (catalogs?.priorities ?? []).map((p) => ({ value: p.id, label: p.name }));
    // impact / urgency catalogs are not portal-visible: they are only offered when the config lists them and the type exposes options later.
    if ((name === 'impact_id' || name === 'urgency_id') && !enumOptions) continue;
    out.push({ name, label: rule?.label ?? base.label ?? name, type: base.type ?? 'text', required: visibility === 'required', readOnly: false, nullable: visibility !== 'required', filterable: false, sortable: false, searchable: false, inCreate: true, inUpdate: false, inList: false, inDetail: true, help: base.help, enum: enumOptions, group: rule?.group });
  }
  for (const [k, rule] of Object.entries(rules)) {
    if (!k.startsWith('custom_fields.') || rule.visibility === 'hidden' || rule.visibility === 'readonly') continue;
    const key = k.slice('custom_fields.'.length);
    out.push({ name: `${CF_PREFIX}${key}`, label: rule.label ?? key.replace(/_/g, ' '), type: 'text', required: rule.visibility === 'required', readOnly: false, nullable: true, filterable: false, sortable: false, searchable: false, inCreate: true, inUpdate: false, inList: false, inDetail: true, group: rule.group ?? 'More' });
  }
  return out;
}

/** Custom fields are named `cf__<key>` on the form (react-hook-form nests dotted names); the API takes `custom_fields: {key: value}`. */
export const CF_PREFIX = 'cf__';
export function toCreateBody(values: Record<string, unknown>): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  const cf: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(values)) {
    if (k.startsWith(CF_PREFIX)) cf[k.slice(CF_PREFIX.length)] = v;
    else if (v !== undefined && v !== '' && v !== null) body[k] = v;
  }
  if (Object.keys(cf).length) body['custom_fields'] = cf;
  return body;
}

export function NewTicketPage() {
  const { me, can } = usePortal();
  const api = useApi();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const types = usePortalGet<TicketTypesResponse>('/tickets/types', { staleTime: 60_000 });
  const [typeId, setTypeId] = useState<string | null>(sp.get('type'));
  const [onBehalf, setOnBehalf] = useState<string>('');
  const colleagues = usePortalList<{ id: string; name: string; email: string | null }>('contacts', {}, { limit: 100, enabled: can('tickets.on_behalf') });
  const [files, setFiles] = useState<File[]>([]);
  const [summary, setSummary] = useState('');
  const onValuesChange = useCallback((v: Record<string, unknown>) => setSummary(typeof v['summary'] === 'string' ? v['summary'] : ''), []);
  const type = useMemo(() => types.data?.data.find((t) => t.id === typeId) ?? null, [types.data, typeId]);
  const fields = useMemo(() => fieldsForType(type?.field_config, types.data), [type, types.data]);
  const create = usePortalMutation(
    async (values: Record<string, unknown>) => {
      const body = { ...toCreateBody(values), type_id: type!.id, ...(onBehalf ? { contact_id: onBehalf } : {}) };
      const row = await api.request<TicketRow>('/v1/tickets', { method: 'POST', body });
      for (const f of files) {
        const fd = new FormData();
        fd.set('file', f, f.name);
        await api.request(`/v1/tickets/${row.id}/attachments`, { method: 'POST', raw: fd });
      }
      return row;
    },
    { invalidate: ['tickets'], success: (r) => `Ticket ${r.ref} logged`, onSuccess: (r) => navigate(`/portal/tickets/${r.id}`) },
  );

  if (!can('tickets.create')) return <EmptyState title="Your portal role cannot log tickets" icon="lock" />;
  if (types.isLoading) return <Loading />;
  if (types.error) return <Banner tone="error">{types.error.detail}</Banner>;
  const list = types.data?.data ?? [];

  if (!type) {
    return (
      <div>
        <BackLink to="/portal/tickets">Tickets</BackLink>
        <PageHeader title="Log a ticket" subtitle="What kind of request is this?" />
        {list.length === 0 ? (
          <EmptyState title="No request types are available" hint={me.branding.support_email ? `Email ${me.branding.support_email} instead.` : undefined} icon="ticket" />
        ) : (
          <div className="pt-types" role="group" aria-label="Request types">
            {list.map((t: TicketType) => (
              <button key={t.id} type="button" className="pt-type" onClick={() => setTypeId(t.id)}>
                <span className="ico"><Icon name={safeIcon(t.icon, 'ticket')} size={18} /></span>
                <span><b>{t.name}</b>{t.description && <small>{t.description}</small>}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="pt-main narrow" style={{ padding: 0 }}>
      <BackLink to="/portal/tickets/new">Request types</BackLink>
      <PageHeader title={type.name} subtitle={type.description ?? undefined} compact />
      {can('tickets.on_behalf') && colleagues.rows.length > 0 && (
        <Field label="On behalf of" help="Leave blank to log it as yourself.">
          <Select value={onBehalf} onChange={(e) => setOnBehalf(e.target.value)} placeholder="Myself" options={colleagues.rows.filter((c) => c.id !== me.contact.id).map((c) => ({ value: c.id, label: c.email ? `${c.name} · ${c.email}` : c.name }))} />
        </Field>
      )}
      {can('kb.view') && <SuggestedArticles text={summary} />}
      <AutoForm
        key={type.id}
        fields={fields}
        mode="create"
        layout="stack"
        submitLabel="Submit ticket"
        busy={create.isPending}
        onCancel={() => setTypeId(null)}
        onSubmit={(values) => create.mutateAsync(values)}
        onValuesChange={onValuesChange}
        footer={me.settings.attachments_enabled && can('tickets.comment') ? <FilePicker files={files} onChange={setFiles} /> : undefined}
      />
    </div>
  );
}

/** Deflection: public articles matching what the customer is typing (debounced; hidden when none). */
export function SuggestedArticles({ text }: { text: string }) {
  const [q, setQ] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setQ(text.trim()), 400);
    return () => clearTimeout(t);
  }, [text]);
  const hits = usePortalGet<{ data: Array<{ id: string; title: string | null; excerpt: string; section: string | null }> }>('/kb-articles/suggest', { query: { q }, enabled: q.length >= 3, staleTime: 60_000 });
  const rows = hits.data?.data ?? [];
  if (q.length < 3 || rows.length === 0) return null;
  return (
    <aside className="pt-suggest" aria-label="Articles that might help">
      <div className="hf-caps">Might this help?</div>
      <ul>
        {rows.map((r) => (
          <li key={r.id}>
            <Link to={`/portal/kb/${r.id}`} target="_blank" rel="noopener">{r.title ?? 'Article'}</Link>
            <small className="hf-muted">{r.section ? `${r.section} — ` : ''}{r.excerpt}</small>
          </li>
        ))}
      </ul>
    </aside>
  );
}

function FilePicker({ files, onChange }: { files: File[]; onChange: (f: File[]) => void }) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <div className="hf-stack" style={{ gap: 'var(--s-2)' }}>
      <div className="hf-row">
        <Button variant="outline" size="sm" icon="paperclip" onClick={() => ref.current?.click()}>Attach files</Button>
        <input ref={ref} type="file" multiple hidden aria-label="Attach files" onChange={(e) => onChange([...files, ...Array.from(e.target.files ?? [])])} />
        {files.length > 0 && <span className="hf-muted" style={{ fontSize: 'var(--t-12)' }}>{files.length} file{files.length === 1 ? '' : 's'}</span>}
      </div>
      {files.length > 0 && (
        <div className="pt-attach">
          {files.map((f, i) => (
            <a key={`${f.name}-${i}`} href="#remove" onClick={(e) => { e.preventDefault(); onChange(files.filter((_, j) => j !== i)); }} title="Remove"><Icon name="file" size={14} /><span>{f.name}</span><Icon name="x" size={12} /></a>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Detail ────────────────────────────────────────────────────────────────────
export function TicketPage() {
  const { id = '' } = useParams();
  const { me } = usePortal();
  const api = useApi();
  const t = usePortalItem<TicketDetail>('tickets', id);
  const [reply, setReply] = useState('');
  const [dialog, setDialog] = useState<null | 'close' | 'reopen' | 'follower'>(null);
  const [note, setNote] = useState('');
  const [followerEmail, setFollowerEmail] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const act = (name: string, body: Record<string, unknown> = {}) => api.resource('tickets').action(name, { id, body });
  const send = usePortalMutation((body: string) => act('actions', { body }), { invalidate: ['tickets', id], success: 'Reply sent', onSuccess: () => setReply('') });
  const close = usePortalMutation((_: void) => act('close', note ? { note } : {}), { invalidate: ['tickets', id], success: 'Ticket closed', onSuccess: () => { setDialog(null); setNote(''); } });
  const reopen = usePortalMutation((_: void) => act('reopen', note ? { note } : {}), { invalidate: ['tickets', id], success: 'Ticket reopened', onSuccess: () => { setDialog(null); setNote(''); } });
  const rate = usePortalMutation((v: { score: number; comment?: string }) => act('csat', v), { invalidate: ['tickets', id], success: 'Thanks for your feedback' });
  const watch = usePortalMutation((email: string) => act('watch', { email }), { invalidate: ['tickets', id], success: 'Follower added', onSuccess: () => { setDialog(null); setFollowerEmail(''); } });
  const upload = usePortalMutation(
    async (list: File[]) => {
      for (const f of list) {
        const fd = new FormData();
        fd.set('file', f, f.name);
        await api.request(`/v1/tickets/${id}/attachments`, { method: 'POST', raw: fd });
      }
    },
    { invalidate: ['tickets', id], success: 'Uploaded' },
  );

  if (t.isLoading) return <Skeleton lines={8} />;
  if (t.error || !t.data) return <EmptyState title={t.error?.status === 404 ? 'Ticket not found' : 'Could not load this ticket'} hint={t.error?.detail} icon="ticket" action={<Link className="hf-btn" to="/portal/tickets">Back to tickets</Link>} />;
  const d = t.data;
  const perm = d.permissions;
  const detailFields = Object.entries(d.field_config ?? {}).filter(([, r]) => r.visibility !== 'hidden');

  return (
    <div>
      <BackLink to="/portal/tickets">Tickets</BackLink>
      <PageHeader
        eyebrow={<span className="hf-mono">{d.ref}{d.type_name ? ` · ${d.type_name}` : ''}</span>}
        title={d.summary}
        badge={<span style={{ marginLeft: 8 }}><StatusPill status={d.status_category} label={d.status_name} kind="ticket" /></span>}
        actions={
          <div className="hf-row">
            {perm.can_reopen && <Button variant="outline" icon="rotate-ccw" onClick={() => setDialog('reopen')}>Reopen</Button>}
            {perm.can_close && <Button variant="outline" icon="check" onClick={() => setDialog('close')}>Close ticket</Button>}
          </div>
        }
      />
      {d.status_is_pending_closure && <Banner tone="info" icon="clock">This ticket is waiting for your confirmation. Reply if it is not fixed, or it will close automatically.</Banner>}
      {perm.can_rate && <CsatCard busy={rate.isPending} onRate={(score, comment) => rate.mutate({ score, comment })} />}
      {d.csat && <Banner tone="pos" icon="star">You rated this ticket {d.csat.score}/5{d.csat.comment ? ` — “${d.csat.comment}”` : ''}.</Banner>}

      <div className="pt-detail">
        <div>
          {d.details && <div className="hf-card pad" style={{ marginBottom: 'var(--s-4)' }}><RichTextView html={d.details} /></div>}
          <Section title="Conversation">
            <Thread entries={d.timeline} meContactId={me.contact.id} />
          </Section>
          {perm.can_comment ? (
            <form className="pt-compose hf-stack" onSubmit={(e) => { e.preventDefault(); if (reply.trim()) send.mutate(reply.trim()); }}>
              <Textarea rows={3} placeholder="Write a reply…" value={reply} onChange={(e) => setReply(e.target.value)} aria-label="Reply" />
              <div className="hf-row">
                {perm.can_attach && (
                  <>
                    <Button variant="ghost" size="sm" icon="paperclip" busy={upload.isPending} onClick={() => fileRef.current?.click()}>Attach</Button>
                    <input ref={fileRef} type="file" multiple hidden aria-label="Attach files" onChange={(e) => { const fs = Array.from(e.target.files ?? []); if (fs.length) upload.mutate(fs); e.target.value = ''; }} />
                  </>
                )}
                <span className="hf-grow" />
                <Button type="submit" variant="primary" icon="send" busy={send.isPending} disabled={!reply.trim()}>Send</Button>
              </div>
            </form>
          ) : (
            <p className="hf-muted" style={{ fontSize: 'var(--t-12)' }}>{d.status_category === 'closed' || d.status_category === 'cancelled' ? 'This ticket is closed.' : 'Your portal role can view this ticket but not reply.'}</p>
          )}
        </div>
        <aside className="hf-stack">
          <dl className="hf-kv">
            <div><dt>Raised by</dt><dd>{d.contact_name ?? '—'}</dd></div>
            <div><dt>Opened</dt><dd>{formatDateTime(d.opened_at)}</dd></div>
            {d.priority_name && <div><dt>Priority</dt><dd>{d.priority_name}</dd></div>}
            {d.category_path && <div><dt>Category</dt><dd>{d.category_path}</dd></div>}
            {d.site_name && <div><dt>Site</dt><dd>{d.site_name}</dd></div>}
            {d.assignee_name && <div><dt>Working on it</dt><dd>{d.assignee_name}</dd></div>}
            {d.due_at && <div><dt>Needed by</dt><dd>{formatDate(d.due_at)}</dd></div>}
            {d.resolved_at && <div><dt>Resolved</dt><dd>{formatDateTime(d.resolved_at)}</dd></div>}
            {detailFields.filter(([k]) => k.startsWith('custom_fields.')).map(([k, r]) => {
              const key = k.slice('custom_fields.'.length);
              const v = d.custom_fields?.[key];
              return v == null || v === '' ? null : <div key={k}><dt>{r.label ?? key.replace(/_/g, ' ')}</dt><dd>{String(v)}</dd></div>;
            })}
          </dl>
          {d.attachments.length > 0 && (
            <div>
              <div className="hf-caps" style={{ marginBottom: 6 }}>Attachments</div>
              <Attachments rows={d.attachments} />
            </div>
          )}
          {d.checklist.length > 0 && (
            <div>
              <div className="hf-caps" style={{ marginBottom: 6 }}>Progress</div>
              {d.checklist.map((c) => (
                <div key={c.id} className={['pt-check', c.done_at && 'done'].filter(Boolean).join(' ')}><Icon name={c.done_at ? 'check-circle' : 'circle'} size={14} />{c.label}</div>
              ))}
            </div>
          )}
          <div>
            <div className="hf-caps hf-between" style={{ marginBottom: 6 }}>Followers {perm.can_comment && <button className="hf-link" type="button" onClick={() => setDialog('follower')} style={{ font: 'inherit' }}>Add</button>}</div>
            {d.watchers.length === 0 ? <span className="hf-muted" style={{ fontSize: 'var(--t-12)' }}>Nobody else follows this ticket.</span> : (
              <div className="hf-row" style={{ flexWrap: 'wrap' }}>{d.watchers.map((w) => <Badge key={w.id} tone="muted" dot={false}>{w.contact_name ?? w.email}</Badge>)}</div>
            )}
          </div>
        </aside>
      </div>

      <Dialog open={dialog === 'close'} onClose={() => setDialog(null)} title="Close this ticket" description="Let us know the issue is resolved. You can reopen it later if it comes back." locked={close.isPending}>
        <div className="hf-stack">
          <Field label="Note (optional)"><Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} /></Field>
          <div className="hf-form-actions"><Button variant="ghost" onClick={() => setDialog(null)}>Cancel</Button><Button variant="primary" busy={close.isPending} onClick={() => close.mutate()}>Close ticket</Button></div>
        </div>
      </Dialog>
      <Dialog open={dialog === 'reopen'} onClose={() => setDialog(null)} title="Reopen this ticket" description="Tell us what is still wrong." locked={reopen.isPending}>
        <div className="hf-stack">
          <Field label="What happened?"><Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} autoFocus /></Field>
          <div className="hf-form-actions"><Button variant="ghost" onClick={() => setDialog(null)}>Cancel</Button><Button variant="primary" busy={reopen.isPending} onClick={() => reopen.mutate()}>Reopen</Button></div>
        </div>
      </Dialog>
      <Dialog open={dialog === 'follower'} onClose={() => setDialog(null)} title="Add a follower" description="They will receive updates on this ticket by email." locked={watch.isPending}>
        <form className="hf-stack" onSubmit={(e) => { e.preventDefault(); if (followerEmail.trim()) watch.mutate(followerEmail.trim()); }}>
          <Field label="Email" required><Input type="email" value={followerEmail} onChange={(e) => setFollowerEmail(e.target.value)} autoFocus /></Field>
          <div className="hf-form-actions"><Button variant="ghost" type="button" onClick={() => setDialog(null)}>Cancel</Button><Button variant="primary" type="submit" busy={watch.isPending} disabled={!followerEmail.trim()}>Add</Button></div>
        </form>
      </Dialog>
    </div>
  );
}

function Thread({ entries, meContactId }: { entries: TimelineEntry[]; meContactId: string }) {
  if (entries.length === 0) return <p className="hf-muted">No updates yet.</p>;
  return (
    <div className="pt-thread">
      {entries.map((e) => {
        const customer = e.actor_kind === 'contact';
        const system = e.actor_kind === 'system' || e.kind === 'status' || e.kind === 'system';
        const who = customer ? (e.contact_id === meContactId ? 'You' : e.contact_name ?? 'Customer') : e.technician_name ?? 'Support';
        return (
          <div key={e.id} className={['pt-msg', customer && 'customer', system && 'system'].filter(Boolean).join(' ')}>
            <Avatar name={who} size="sm" />
            <div>
              <div className="who"><b>{who}</b><span>{formatDateTime(e.created_at)}</span>{e.outcome_name && !system && <span>{e.outcome_name}</span>}{e.status_after_name && <span>→ {e.status_after_name}</span>}</div>
              <div className="bubble">{e.body ? <RichTextView html={e.body} /> : <span className="hf-prewrap">{e.body_text}</span>}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Attachments({ rows }: { rows: AttachmentRow[] }) {
  const api = useApi();
  return (
    <div className="pt-attach">
      {rows.map((a) => (
        <a key={a.id} href={portalUrl(api, `/v1/attachments/${a.id}/download`)} target="_blank" rel="noopener noreferrer" title={a.filename}><Icon name="paperclip" size={14} /><span>{a.filename}</span><small className="hf-muted">{fmtBytes(a.bytes)}</small></a>
      ))}
    </div>
  );
}

function CsatCard({ busy, onRate }: { busy: boolean; onRate: (score: number, comment?: string) => void }) {
  const [score, setScore] = useState(0);
  const [comment, setComment] = useState('');
  return (
    <div className="hf-card pad hf-stack" style={{ marginBottom: 'var(--s-4)' }} aria-label="Rate this ticket">
      <div><b>How did we do?</b><div className="hf-muted" style={{ fontSize: 'var(--t-12)' }}>Your rating helps the team improve.</div></div>
      <div className="pt-stars" role="radiogroup" aria-label="Score">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" role="radio" aria-checked={score === n} aria-pressed={n <= score} aria-label={`${n} star${n === 1 ? '' : 's'}`} onClick={() => setScore(n)}>★</button>
        ))}
      </div>
      {score > 0 && (
        <>
          <Textarea rows={2} placeholder="Anything to add? (optional)" value={comment} onChange={(e) => setComment(e.target.value)} aria-label="Comment" />
          <div className="hf-row"><span className="hf-grow" /><Button variant="primary" busy={busy} onClick={() => onRate(score, comment.trim() || undefined)}>Send rating</Button></div>
        </>
      )}
    </div>
  );
}
